import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { getSurveyOptionFromValue } from "../../../utils/surveyUtils";
import { NavLink } from "react-router-dom";

function SurveyItem({ survey, handleDelete, handleEdit, handleQR, mode = "admin" }) {
  const isMemberView = mode === "member";
  const questionCount = Array.isArray(survey?.surveyQuestions)
    ? survey.surveyQuestions.length
    : 0;
  const hasTargeting = Boolean(survey?.targeting?.enabled);

  return (
    <Box key={survey._id}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 14px 28px rgba(15, 23, 42, 0.14)",
          },
        }}
      >
        <CardContent>
          <Typography gutterBottom sx={{ color: "text.secondary", fontSize: 14 }}>
            {survey.surveyType === "employee"
              ? "Mitarbeiterbefragung"
              : "Patientenbefragung"}
          </Typography>
          <Typography variant="h5" component="div" sx={{ mb: 1 }}>
            {survey.surveyTitle}
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 1.75 }}>
            {survey.surveyDescription || "Keine Beschreibung hinterlegt."}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip label={`${questionCount} Fragen`} size="small" />
            {!isMemberView && (
              <Chip
                label={`${survey?.surveyAnswers?.length || 0} Antworten`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {hasTargeting && (
              <Chip
                label="Mitglieder-Zielgruppe"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>

          <Box sx={{ fontSize: "0.875rem" }}>
            {survey && survey.surveyQuestions.slice(0, 2).map((q, index) => (
              <Box key={`${survey._id}-${index}`} sx={{ mb: 0.8 }}>
                <span>
                  {index + 1}. {q.question} <br />
                </span>
                <span style={{ marginLeft: 15 }}>
                  Art:{" "}
                  {q.type && getSurveyOptionFromValue(q.type)?.label
                    ? getSurveyOptionFromValue(q.type).label
                    : "Unbekannt"}
                </span>
              </Box>
            ))}
            {questionCount > 2 && (
              <Typography variant="caption" color="text.secondary">
                +{questionCount - 2} weitere Fragen
              </Typography>
            )}
          </Box>
          {!isMemberView && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography>Antworten: {survey?.surveyAnswers?.length}</Typography>
            </>
          )}
        </CardContent>
        <CardActions>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column", // Stack buttons vertically
              gap: 1, // Space between buttons
              width: "100%", // Make buttons take full width
            }}
          >
            <Button
              component={NavLink}
              to={`/survey/${survey._id}`}
              size="small"
              fullWidth
              color="primary"
              variant="contained"
            >
              ANZEIGEN
            </Button>
            {!isMemberView && (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  color="secondary"
                  onClick={() => handleEdit(survey)}
                >
                  BEARBEITEN
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(survey._id, survey.surveyTitle)}
                >
                  LÖSCHEN
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  color="warning"
                  onClick={() => handleQR(survey)}
                >
                  QR Code
                </Button>
                <Button
                  component={NavLink}
                  to={`/dashboard/survey/evaluate/${survey._id}`}
                  size="small"
                  fullWidth
                  variant="contained"
                  color="primary"
                >
                  Auswertung anzeigen
                </Button>
              </>
            )}
          </Box>
        </CardActions>
      </Card>
    </Box>
  );
}

SurveyItem.propTypes = {
  survey: PropTypes.object,
  handleDelete: PropTypes.func,
  handleEdit: PropTypes.func,
  handleQR: PropTypes.func,
  mode: PropTypes.oneOf(["admin", "member"]),
};

export default SurveyItem;
